import React from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import CustomerService from '../../Services/CustomerService';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import { TbCircleFilled } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import 'react-phone-input-2/lib/style.css'
import { useCartContext } from '../../Contexts/CartContext';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useTranslation } from 'react-i18next';
import { TokenManager } from '../../Services/TokenManager';
import ButtonQuickPurchase from './ButtonQuickPurchase';
import { useLoginHook } from '../../Hooks/LoginHook';
import { useNavigate } from 'react-router-dom';
import { useCartHook } from '../../Hooks/CartHook';

const getValidationSchema = (t: any) =>
    Yup.object().shape({
        email: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .email(t('form.validation.invalid_email'))
            .required(t('form.validation.required')),
        password: Yup.string()
            .required(t('form.validation.required')),
});

interface FormLoginProps {
    onCancel: CallableFunction;
}

const FormLogin = ({onCancel}: FormLoginProps) => {

    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);
    const { orderType, isStoreCounter } = useOrderTypeContext();
    const { setUser, setShowLoginBasic, setShowForgotPassword, setShowLoginStoreCounter } = useLoginContext();
    const { setShowCart, returnToCheckout, setReturnToCheckout } = useCartContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingIconNoSearchUser, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setShowMainMenu } = useLayoutContext();
    const { getLinkToHome } = useStoreHook();
    const { handleRedirect } = useLoginHook();
    const navigate = useNavigate();
    const { getCustomerCart } = useCartHook();

    const initialData = {
        email: '',
        password: '',
    }

    const handleLogin = async (data: any) => {
        const payload = {
            email: data.email,
            password: data.password,
        }
        try {
            setMainLoading(true);
            setMainLoadingTitle(t('login.sso_title'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));
            const customerService = new CustomerService(orderType);
            const response = await customerService.login(payload);
            if (response) {
                setMainLoading(false);

                TokenManager.setToken(response.token);
                setUser(response.profile);
                setShowLoginBasic(false);
                setShowCart(false);
                setShowMainMenu(false);

                if (!isStoreCounter) {
                    getCustomerCart();
                    setShowLoginStoreCounter(false);
                }

                //* Redirect
                handleRedirect(returnToCheckout ? `${getLinkToHome()}/checkout` : '');
                setReturnToCheckout(false);

            }
        } catch (error) {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconNoSearchUser(true);
            setMainLoadingTitle(t('login.error_invalid_credentials'));
            setMainLoadingMessage('');
            setMainLoadingCloseButton(true);
        }
    }

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field.tests.some((test: any) => test.name === 'required');
    };

    const handleRegister = () => {
        setShowMainMenu(false);
        setShowLoginBasic(false);
        setShowCart(false);
        setShowLoginStoreCounter(false);
        navigate('/cadastro');
    }

    return (
        <>
            <Formik
                initialValues={initialData}
                validationSchema={validationSchema}
                onSubmit={(data) => {
                    handleLogin(data);
                }}
                >
                {({ setFieldValue, values }) => (
                    <Form className="flex flex-col gap-4 justify-between">

                        <div className='h-full grid gap-3 p-4 bg-gray-50 rounded-xl'>

                            <div className='flex flex-col gap-3'>
                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.form.email.label')}
                                        {isFieldRequired(validationSchema, 'email') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <input
                                        id="email"
                                        type='email'
                                        name="email"
                                        value={values.email}
                                        onChange={(e) => setFieldValue('email', e.target.value)}
                                        autoComplete='on'
                                        autoFocus
                                        placeholder={t('login.form.email.placeholder')}
                                        className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                    <ErrorMessage name="email" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>

                                <div className='w-full text-left'>
                                    <span className='font-semibold text-shopfood-title'>
                                        {t('login.form.password.label')}
                                        {isFieldRequired(validationSchema, 'password') && (<sup className="text-shopfood-error inline-flex ml-0.5"> <TbCircleFilled size={7} /></sup>)}
                                    </span>
                                    <input
                                        id="password"
                                        name="password"
                                        type='password'
                                        value={values.password}
                                        onChange={(e) => setFieldValue('password', e.target.value)}
                                        className="relative block w-full rounded border-0 p-2 ring-1 ring-gray-300 placeholder:text-gray-300 placeholder:font-normal font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200"
                                    />
                                    <ErrorMessage name="password" component="div" className='text-xs text-shopfood-error pt-1' />
                                </div>


                            </div>
                        </div>

                        <div className='flex items-center justify-between px-6 mb-4 text-sm leading-none justify-between gap-4'>
                            <div className='text-gray-400 hover:text-custom-primary transition-all ease-in-out w-full text-left hover:text-sky-500 cursor-pointer' onClick={() => setShowForgotPassword(true)}> {t('login.forgot_password.title')}</div>
                            <div className='text-gray-400 hover:text-custom-primary transition-all ease-in-out w-full text-right hover:text-sky-500 cursor-pointer' onClick={handleRegister}>{t('login.button_register_label')}</div>
                        </div>

                        <div className='flex gap-2 p-2 pt-0 flex-wrap'>
                            <ButtonPrimary type={'submit'} className='gap-1 flex-1 w-full' onClick={() => {}}>
                                <span>{t('login.form.button_submit.label')}</span>
                            </ButtonPrimary>

                            <ButtonQuickPurchase className='flex-1 text-nowrap  w-full' />
                        </div>

                    </Form>
                )}
            </Formik>
        </>
    )
}

export default FormLogin;
